import React from 'react';
import { Signal } from '@preact/signals-react';
import TextEdit from '../../molecules/TextEdit';
import SelectTextField from '../../molecules/SelectTextField';

const FORMAT_OPTIONS = [
    { value: 'text', label: 'Texto' },
    { value: 'number', label: 'Número' },
    { value: 'currency', label: 'Moeda' },
    { value: 'date', label: 'Data' },
    { value: 'phone', label: 'Telefone' },
    { value: 'email', label: 'E-mail' },
    { value: 'govTaxId', label: 'CNPJ' },
    { value: 'socialId', label: 'CPF' },
    { value: 'fileUrl', label: 'URL' },
];

type TemplateVariable = {
    name: string;
    display: string;
    field: string;
    format: string;
    type?: string;
    size?: number;
    properties?: Array<{
        name: string;
        display?: string;
        format: string;
        size?: number;
    }>;
    error?: string;
}

interface TemplateVariableModalFormProps {
    show: Signal<boolean>;
    variable: Signal<TemplateVariable>;
    onAdd: (index: number, updates: Partial<TemplateVariable>) => void;
    existingVariables: TemplateVariable[];
    editingIndex?: number;
}

const TemplateVariableModalForm: React.FC<TemplateVariableModalFormProps> = ({ show, variable, onAdd, existingVariables, editingIndex = -1 }) => {
    if (!show.value) return null;

    const isEditing = editingIndex >= 0;
    const isNameDuplicate = existingVariables.some((v, i) => i !== editingIndex && v.name === variable.value.name && variable.value.name !== '');
    const isFieldDuplicate = existingVariables.some((v, i) => i !== editingIndex && v.field === variable.value.field && variable.value.field !== '');
    
    const isFormValid = () => {
        return variable.value.name.trim() !== '' &&
               variable.value.display.trim() !== '' &&
               variable.value.field.trim() !== '' &&
               !isNameDuplicate &&
               !isFieldDuplicate;
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white dark:bg-slate-800 rounded-lg p-6 w-96 max-w-full">
                <h3 className="text-lg font-semibold mb-4 dark:text-white">
                    {isEditing ? 'Editar Variável' : 'Nova Variável'}
                </h3>

                <div className="space-y-4">
                    <TextEdit
                        label="Nome da variável:"
                        value={variable.value.name}
                        onChange={(e) => {
                            if (!isEditing) {
                                const formatted = e.target.value
                                    .toUpperCase()
                                    .replace(/[^A-Z0-9_-]/g, '')
                                    .slice(0, 40);
                                variable.value = { ...variable.value, name: formatted };
                            }
                        }}
                        placeholder=""
                        maxLength={40}
                        helperText={isEditing ? 'Nome detectado automaticamente (não editável)' : 'Identificador único'}
                        errorMessage={isNameDuplicate ? 'Nome já existe' : ''}
                        disabled={isEditing}
                    />

                    <TextEdit
                        label="Descrição:"
                        value={variable.value.display}
                        onChange={(e) => variable.value = { ...variable.value, display: e.target.value }}
                        placeholder=""
                        maxLength={100}
                        helperText='Rótulo do campo e coluna da tabela'
                    />

                    <TextEdit
                        label="Nome do campo:"
                        value={variable.value.field}
                        onChange={(e) => {
                            const formatted = e.target.value
                                .replace(/[^a-zA-Z0-9]/g, '')
                                .replace(/^\s+|\s+$/g, '');
                            variable.value = { ...variable.value, field: formatted };
                        }}
                        placeholder=""
                        maxLength={50}
                        helperText='Nome interno (camelCase)'
                        errorMessage={isFieldDuplicate ? 'Campo já existe' : ''}
                    />

                    {variable.value.type !== 'array' && (
                        <SelectTextField
                            label="Formato:"
                            value={variable.value.format}
                            onChange={(e) => variable.value = { ...variable.value, format: e.target.value }}
                            items={FORMAT_OPTIONS}
                            emptyLabel="Selecione o formato"
                            disabled={variable.value.type === 'email'}
                        />
                    )}
                    
                    {variable.value.type === 'email' && (
                        <p className="text-xs text-gray-500 dark:text-gray-400 italic">
                            Formato fixo: E-mail (detectado automaticamente dos campos From/To/CC/BCC/Reply-To)
                        </p>
                    )}

                    {(variable.value.format === 'text' || variable.value.format === 'number') && variable.value.type !== 'array' && (
                        <TextEdit
                            label="Tamanho máximo:"
                            value={variable.value.size?.toString() || ""}
                            onChange={(e) => {
                                const val = parseInt(e.target.value) || 0;
                                const size = Math.min(Math.max(val, 1), 150);
                                variable.value = { ...variable.value, size };
                            }}
                            type="number"
                            placeholder=""
                            helperText="Limite: 1 a 150 caracteres"
                        />
                    )}
                    
                    {variable.value.type === 'array' && variable.value.properties && variable.value.properties.length > 0 && (
                        <div className="border-t border-gray-200 dark:border-slate-600 pt-4 mt-4">
                            <h4 className="text-sm font-semibold mb-3 dark:text-white">Propriedades do Array:</h4>
                            <div className="space-y-2 max-h-60 overflow-y-auto">
                                {variable.value.properties.map((prop, i) => (
                                    <div key={i} className="p-2 bg-gray-50 dark:bg-slate-700 rounded">
                                        <p className="text-xs font-mono text-purple-600 dark:text-purple-400 mb-2">{prop.name}</p>
                                        <TextEdit
                                            label=""
                                            value={prop.display || ''}
                                            onChange={(e) => {
                                                const newProps = [...(variable.value.properties || [])];
                                                newProps[i] = { ...newProps[i], display: e.target.value };
                                                variable.value = { ...variable.value, properties: newProps };
                                            }}
                                            placeholder="Descrição"
                                            maxLength={100}
                                        />
                                        <div className="grid grid-cols-2 gap-2 mt-2">
                                            <SelectTextField
                                                label=""
                                                value={prop.format}
                                                onChange={(e) => {
                                                    const newProps = [...(variable.value.properties || [])];
                                                    newProps[i] = { ...newProps[i], format: e.target.value };
                                                    variable.value = { ...variable.value, properties: newProps };
                                                }}
                                                items={FORMAT_OPTIONS}
                                                emptyLabel="Formato"
                                            />
                                            {(prop.format === 'text' || prop.format === 'number') && (
                                                <TextEdit
                                                    label=""
                                                    value={prop.size?.toString() || "50"}
                                                    onChange={(e) => {
                                                        const val = parseInt(e.target.value) || 50;
                                                        const size = Math.min(Math.max(val, 1), 150);
                                                        const newProps = [...(variable.value.properties || [])];
                                                        newProps[i] = { ...newProps[i], size };
                                                        variable.value = { ...variable.value, properties: newProps };
                                                    }}
                                                    type="number"
                                                    placeholder="Tamanho"
                                                />
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>

                <div className="flex justify-end space-x-2 mt-6">
                    <button
                        type="button"
                        onClick={() => show.value = false}
                        className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300 dark:bg-slate-700 dark:hover:bg-slate-600 dark:text-white"
                    >
                        Cancelar
                    </button>
                    <button
                        type="button"
                        onClick={() => {
                            const { name, field, ...updates } = variable.value;
                            onAdd(editingIndex, updates);
                        }}
                        disabled={!isFormValid()}
                        className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700 dark:bg-gray-500 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {isEditing ? 'Salvar' : 'Adicionar'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default TemplateVariableModalForm;
